import React, { useState, useEffect } from "react";
import { getPhysician, createPhysician, updatePhysician } from "../api/physicians.js";
import { useHierarchy } from "../contexts/HierarchyContext.jsx";

const ONCOLOGY_SUBSPECIALTIES = [
  { value: "hematology", label: "Hematology (blood disorders, lymphoma, leukemia, myeloma)" },
  { value: "solid-breast-gyn", label: "Solid Tumors — Breast & GYN" },
  { value: "solid-thoracic-gi", label: "Solid Tumors — Thoracic & GI" },
  { value: "solid-gu", label: "Solid Tumors — GU/Prostate" },
  { value: "medical-general", label: "Medical Oncology — General" },
  { value: "medical-integrative", label: "Medical Oncology — Integrative" },
];

const ORTHO_SUBSPECIALTIES = [
  { value: "joint-replacement-hip-knee", label: "Joint Replacement — Hip & Knee" },
  { value: "joint-replacement-shoulder", label: "Joint Replacement — Shoulder" },
  { value: "sports-medicine-operative", label: "Sports Medicine — Operative" },
  { value: "sports-medicine-non-op", label: "Sports Medicine — Non-Operative" },
  { value: "hand-upper-extremity", label: "Hand & Upper Extremity" },
  { value: "foot-ankle", label: "Foot & Ankle" },
  { value: "spine", label: "Spine" },
  { value: "trauma-fracture-care", label: "Trauma / Fracture Care" },
  { value: "pediatric-orthopedics", label: "Pediatric Orthopedics" },
  { value: "physical-medicine-rehab", label: "Physical Medicine & Rehabilitation" },
  { value: "pain-management", label: "Pain Management" },
  { value: "general-orthopedics", label: "General Orthopedics" },
];

const BODY_PARTS = [
  "knee", "hip", "shoulder", "elbow", "wrist-hand", "ankle-foot",
  "spine-cervical", "spine-lumbar", "tibia",
];

const PROVIDER_TYPES = [
  { value: "physician", label: "Physician (MD/DO)" },
  { value: "pa-c", label: "Physician Assistant (PA-C)" },
  { value: "np", label: "Nurse Practitioner (NP)" },
  { value: "do", label: "Doctor of Osteopathy (DO)" },
];

const SURGICAL_PHILOSOPHIES = [
  { value: "surgery-first", label: "Surgery-first" },
  { value: "conservative-first", label: "Conservative-first" },
  { value: "non-operative-only", label: "Non-operative only" },
];

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const EMPTY_OFFICE = { id: "", name: "", address: "", city: "", state: "", zip: "", phone: "" };

function buildEmptyForm(isOrtho) {
  return {
    name: "",
    credentials: "",
    npi: "",
    providerType: "physician",
    gender: "",
    subspecialties: isOrtho ? ["general-orthopedics"] : ["medical-general"],
    focusArea: "",
    boardCertifications: [],
    fellowships: [],
    bodyParts: [],
    conditionsTreated: [],
    proceduresPerformed: [],
    location: isOrtho ? null : { name: "", address: "", city: "", state: "", zip: "", phone: "" },
    offices: isOrtho ? [{ ...EMPTY_OFFICE, name: "Main Office" }] : [],
    operative: isOrtho ? true : undefined,
    surgicalPhilosophy: isOrtho ? "conservative-first" : "",
    treatsAcuteInjury: isOrtho ? true : undefined,
    treatsDegenerative: isOrtho ? true : undefined,
    treatsPediatric: isOrtho ? false : undefined,
    acceptsSecondOpinions: isOrtho ? true : undefined,
    acceptsPostOpFollowup: isOrtho ? true : undefined,
    insurancesAccepted: [],
    languages: ["English"],
    ageRange: { min: 18, max: 99 },
    acceptingNewPatients: true,
    daysAvailable: ["Mon", "Tue", "Wed", "Thu", "Fri"],
    avgDaysToNextAvailable: 7,
    sameDayAvailabilityLikely: false,
    bio: "",
    clinicalInterests: "",
    preferences: "",
    patientPopulation: "",
  };
}

export default function PhysicianEditor({ navigate, physicianId }) {
  const isEdit = !!physicianId;
  const { currentSubTenant: currentTenant } = useHierarchy();
  const isOrtho = currentTenant?.specialty === "orthopedics";
  const SUBSPECIALTY_OPTIONS = isOrtho ? ORTHO_SUBSPECIALTIES : ONCOLOGY_SUBSPECIALTIES;

  const [form, setForm] = useState(() => buildEmptyForm(isOrtho));
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit);

  // Temp inputs for tag-style fields
  const [conditionInput, setConditionInput] = useState("");
  const [procedureInput, setProcedureInput] = useState("");
  const [insuranceInput, setInsuranceInput] = useState("");
  const [languageInput, setLanguageInput] = useState("");
  const [boardCertInput, setBoardCertInput] = useState("");
  const [fellowshipInput, setFellowshipInput] = useState("");

  useEffect(() => {
    if (!isEdit) return;
    getPhysician(physicianId)
      .then((p) => {
        const base = buildEmptyForm(isOrtho);
        // Handle legacy records that only have `subspecialty` (singular)
        let subs = Array.isArray(p.subspecialties) ? p.subspecialties : [];
        if (subs.length === 0 && p.subspecialty) subs = [p.subspecialty];
        if (subs.length === 0) subs = [isOrtho ? "general-orthopedics" : "medical-general"];

        setForm({
          ...base,
          ...p,
          subspecialties: subs,
          location: isOrtho
            ? null
            : { ...(base.location || {}), ...(p.location || {}) },
          offices: Array.isArray(p.offices) && p.offices.length > 0
            ? p.offices
            : (isOrtho ? [{ ...EMPTY_OFFICE, name: "Main Office" }] : []),
          ageRange: { ...base.ageRange, ...(p.ageRange || {}) },
          boardCertifications: p.boardCertifications || [],
          fellowships: p.fellowships || [],
          bodyParts: p.bodyParts || [],
          conditionsTreated: p.conditionsTreated || [],
          proceduresPerformed: p.proceduresPerformed || [],
          insurancesAccepted: p.insurancesAccepted || [],
          languages: p.languages || ["English"],
          daysAvailable: p.daysAvailable || [],
        });
      })
      .catch((err) => alert("Failed to load physician: " + err.message))
      .finally(() => setLoading(false));
  }, [isEdit, physicianId, isOrtho]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setLocationField = (k, v) => setForm((f) => ({ ...f, location: { ...(f.location || {}), [k]: v } }));
  const setAgeField = (k, v) => setForm((f) => ({ ...f, ageRange: { ...f.ageRange, [k]: parseInt(v, 10) || 0 } }));

  // Office handlers (orthopedic multi-office)
  const addOffice = () => setField("offices", [...(form.offices || []), { ...EMPTY_OFFICE }]);
  const removeOffice = (idx) => setField("offices", (form.offices || []).filter((_, i) => i !== idx));
  const updateOfficeField = (idx, key, value) => {
    const next = (form.offices || []).map((o, i) => (i === idx ? { ...o, [key]: value } : o));
    setField("offices", next);
  };

  const toggleBodyPart = (part) => {
    if (form.bodyParts.includes(part)) {
      setField("bodyParts", form.bodyParts.filter((p) => p !== part));
    } else {
      setField("bodyParts", [...form.bodyParts, part]);
    }
  };

  // Tag-style list helpers
  const addToList = (listName, inputValue, setInput) => {
    const v = inputValue.trim();
    if (!v || form[listName].includes(v)) return;
    setField(listName, [...form[listName], v]);
    setInput("");
  };
  const removeFromList = (listName, item) => {
    setField(listName, form[listName].filter((i) => i !== item));
  };

  const toggleDay = (day) => {
    if (form.daysAvailable.includes(day)) {
      setField("daysAvailable", form.daysAvailable.filter((d) => d !== day));
    } else {
      setField("daysAvailable", [...form.daysAvailable, day]);
    }
  };

  const toggleSubspecialty = (value) => {
    if (form.subspecialties.includes(value)) {
      // Don't allow removing the last subspecialty
      if (form.subspecialties.length === 1) return;
      setField("subspecialties", form.subspecialties.filter((s) => s !== value));
    } else {
      setField("subspecialties", [...form.subspecialties, value]);
    }
  };

  const handleSave = async () => {
    if (!form.name.trim()) return alert("Name is required");
    if (!form.subspecialties || form.subspecialties.length === 0) return alert("At least one subspecialty is required");

    setSaving(true);
    try {
      if (isEdit) {
        await updatePhysician(physicianId, form);
      } else {
        await createPhysician(form);
      }
      navigate("physicians");
    } catch (err) {
      alert("Save failed: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div style={s.loading}>Loading...</div>;

  return (
    <div>
      <div style={s.header}>
        <button style={s.backBtn} onClick={() => navigate("physicians")}>← Back</button>
        <div>
          <h1 style={s.title}>{isEdit ? "Edit Physician" : "New Physician"}</h1>
          {isEdit && <div style={s.subtitle}>{physicianId}</div>}
        </div>
      </div>

      <div style={s.body}>
        {/* ─── Basic Info ──────────────────────── */}
        <Section title="Basic Information">
          <div style={s.row}>
            <Field label="Name *" flex={2}>
              <input style={s.input} value={form.name} onChange={(e) => setField("name", e.target.value)} placeholder="Dr. Jane Smith" />
            </Field>
            <Field label="NPI" flex={1}>
              <input style={s.input} value={form.npi || ""} onChange={(e) => setField("npi", e.target.value)} placeholder="1234567890" />
            </Field>
          </div>
          <Field label="Credentials">
            <input style={s.input} value={form.credentials} onChange={(e) => setField("credentials", e.target.value)} placeholder="MD, Board-certified Medical Oncology" />
          </Field>
          <Field label="Subspecialties *" hint="Select all that apply. A physician can have multiple subspecialties.">
            <div style={s.subspecialtyGrid}>
              {SUBSPECIALTY_OPTIONS.map((o) => {
                const active = form.subspecialties.includes(o.value);
                return (
                  <button
                    key={o.value}
                    type="button"
                    style={active ? s.subSpecPillActive : s.subSpecPill}
                    onClick={() => toggleSubspecialty(o.value)}
                  >
                    <span style={s.subSpecCheck}>{active ? "✓" : ""}</span>
                    {o.label}
                  </button>
                );
              })}
            </div>
          </Field>
          <Field label="Focus Area">
            <input style={s.input} value={form.focusArea} onChange={(e) => setField("focusArea", e.target.value)} placeholder="Breast & GYN Oncology" />
          </Field>
          <Field label="Board Certifications">
            <TagInput
              items={form.boardCertifications}
              input={boardCertInput}
              setInput={setBoardCertInput}
              onAdd={() => addToList("boardCertifications", boardCertInput, setBoardCertInput)}
              onRemove={(item) => removeFromList("boardCertifications", item)}
              placeholder="Add certification and press Enter"
            />
          </Field>
        </Section>

        {/* ─── Conditions & Procedures ────────── */}
        <Section title="Conditions & Procedures">
          <Field label="Conditions Treated" hint="These drive the match engine's condition scoring">
            <TagInput
              items={form.conditionsTreated}
              input={conditionInput}
              setInput={setConditionInput}
              onAdd={() => addToList("conditionsTreated", conditionInput, setConditionInput)}
              onRemove={(item) => removeFromList("conditionsTreated", item)}
              placeholder="e.g., DLBCL, Breast Cancer Stage II"
            />
          </Field>
          <Field label="Procedures Performed">
            <TagInput
              items={form.proceduresPerformed}
              input={procedureInput}
              setInput={setProcedureInput}
              onAdd={() => addToList("proceduresPerformed", procedureInput, setProcedureInput)}
              onRemove={(item) => removeFromList("proceduresPerformed", item)}
              placeholder="e.g., Bone marrow biopsy"
            />
          </Field>
        </Section>

        {/* ─── Location / Offices ─────────────── */}
        {!isOrtho ? (
          <Section title="Location" hint="This physician record represents this doctor at this specific location. Create a separate record for each location.">
            <div style={s.row}>
              <Field label="Location Name" flex={2}>
                <input style={s.input} value={form.location?.name || ""} onChange={(e) => setLocationField("name", e.target.value)} placeholder="Main Campus" />
              </Field>
              <Field label="Phone" flex={1}>
                <input style={s.input} value={form.location?.phone || ""} onChange={(e) => setLocationField("phone", e.target.value)} placeholder="555-123-4567" />
              </Field>
            </div>
            <Field label="Address">
              <input style={s.input} value={form.location?.address || ""} onChange={(e) => setLocationField("address", e.target.value)} placeholder="123 Medical Drive" />
            </Field>
            <div style={s.row}>
              <Field label="City" flex={2}>
                <input style={s.input} value={form.location?.city || ""} onChange={(e) => setLocationField("city", e.target.value)} placeholder="Riverside" />
              </Field>
              <Field label="State" flex={1}>
                <input style={s.input} value={form.location?.state || ""} onChange={(e) => setLocationField("state", e.target.value)} placeholder="CA" maxLength={2} />
              </Field>
              <Field label="Zip" flex={1}>
                <input style={s.input} value={form.location?.zip || ""} onChange={(e) => setLocationField("zip", e.target.value)} placeholder="92501" />
              </Field>
            </div>
          </Section>
        ) : (
          <Section title="Offices" hint="Orthopedic providers typically rotate across multiple clinic offices. Add every office where this provider sees patients.">
            {(form.offices || []).map((office, idx) => (
              <div key={idx} style={s.officeBlock}>
                <div style={s.officeHeader}>
                  <div style={s.officeHeaderLabel}>Office #{idx + 1}</div>
                  {(form.offices || []).length > 1 && (
                    <button type="button" style={s.removeBtn} onClick={() => removeOffice(idx)}>Remove</button>
                  )}
                </div>
                <div style={s.row}>
                  <Field label="Office Name" flex={2}>
                    <input style={s.input} value={office.name} onChange={(e) => updateOfficeField(idx, "name", e.target.value)} placeholder="Shirlington" />
                  </Field>
                  <Field label="Phone" flex={1}>
                    <input style={s.input} value={office.phone} onChange={(e) => updateOfficeField(idx, "phone", e.target.value)} placeholder="703-525-6100" />
                  </Field>
                </div>
                <Field label="Address">
                  <input style={s.input} value={office.address} onChange={(e) => updateOfficeField(idx, "address", e.target.value)} placeholder="2800 Shirlington Rd" />
                </Field>
                <div style={s.row}>
                  <Field label="City" flex={2}>
                    <input style={s.input} value={office.city} onChange={(e) => updateOfficeField(idx, "city", e.target.value)} placeholder="Arlington" />
                  </Field>
                  <Field label="State" flex={1}>
                    <input style={s.input} value={office.state} onChange={(e) => updateOfficeField(idx, "state", e.target.value)} placeholder="VA" maxLength={2} />
                  </Field>
                  <Field label="Zip" flex={1}>
                    <input style={s.input} value={office.zip} onChange={(e) => updateOfficeField(idx, "zip", e.target.value)} placeholder="22206" />
                  </Field>
                </div>
              </div>
            ))}
            <button type="button" style={s.addBtn} onClick={addOffice}>+ Add another office</button>
          </Section>
        )}

        {/* ─── Orthopedic-only: Provider Profile ─── */}
        {isOrtho && (
          <Section title="Provider Profile" hint="Used by the orthopedic match engine to route patients to the right provider.">
            <div style={s.row}>
              <Field label="Provider Type" flex={1}>
                <select style={s.input} value={form.providerType} onChange={(e) => setField("providerType", e.target.value)}>
                  {PROVIDER_TYPES.map((p) => (
                    <option key={p.value} value={p.value}>{p.label}</option>
                  ))}
                </select>
              </Field>
              <Field label="Gender" flex={1}>
                <select style={s.input} value={form.gender || ""} onChange={(e) => setField("gender", e.target.value)}>
                  <option value="">—</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              </Field>
            </div>
            <Field label="Body Parts Treated" hint="Hard requirement for the match engine — wrong body part tanks the match score.">
              <div style={s.daysRow}>
                {BODY_PARTS.map((part) => (
                  <button
                    key={part}
                    type="button"
                    style={form.bodyParts.includes(part) ? s.dayPillActive : s.dayPill}
                    onClick={() => toggleBodyPart(part)}
                  >
                    {part}
                  </button>
                ))}
              </div>
            </Field>
            <div style={s.row}>
              <Field label="Operative?" flex={1}>
                <label style={s.toggle}>
                  <input type="checkbox" checked={!!form.operative} onChange={(e) => setField("operative", e.target.checked)} />
                  <span>{form.operative ? "Performs surgery" : "Non-operative only"}</span>
                </label>
              </Field>
              <Field label="Surgical Philosophy" flex={2}>
                <select style={s.input} value={form.surgicalPhilosophy || ""} onChange={(e) => setField("surgicalPhilosophy", e.target.value)}>
                  <option value="">—</option>
                  {SURGICAL_PHILOSOPHIES.map((p) => (
                    <option key={p.value} value={p.value}>{p.label}</option>
                  ))}
                </select>
              </Field>
            </div>
            <Field label="Clinical Scope">
              <div style={s.checkboxGrid}>
                <label style={s.toggle}>
                  <input type="checkbox" checked={!!form.treatsAcuteInjury} onChange={(e) => setField("treatsAcuteInjury", e.target.checked)} />
                  <span>Treats acute injuries</span>
                </label>
                <label style={s.toggle}>
                  <input type="checkbox" checked={!!form.treatsDegenerative} onChange={(e) => setField("treatsDegenerative", e.target.checked)} />
                  <span>Treats degenerative conditions</span>
                </label>
                <label style={s.toggle}>
                  <input type="checkbox" checked={!!form.treatsPediatric} onChange={(e) => setField("treatsPediatric", e.target.checked)} />
                  <span>Treats pediatric patients</span>
                </label>
                <label style={s.toggle}>
                  <input type="checkbox" checked={!!form.acceptsSecondOpinions} onChange={(e) => setField("acceptsSecondOpinions", e.target.checked)} />
                  <span>Accepts second opinions</span>
                </label>
                <label style={s.toggle}>
                  <input type="checkbox" checked={!!form.acceptsPostOpFollowup} onChange={(e) => setField("acceptsPostOpFollowup", e.target.checked)} />
                  <span>Accepts post-op follow-up</span>
                </label>
                <label style={s.toggle}>
                  <input type="checkbox" checked={!!form.sameDayAvailabilityLikely} onChange={(e) => setField("sameDayAvailabilityLikely", e.target.checked)} />
                  <span>Same-day availability likely</span>
                </label>
              </div>
            </Field>
            <Field label="Fellowships">
              <TagInput
                items={form.fellowships || []}
                input={fellowshipInput}
                setInput={setFellowshipInput}
                onAdd={() => addToList("fellowships", fellowshipInput, setFellowshipInput)}
                onRemove={(item) => removeFromList("fellowships", item)}
                placeholder="e.g., Sports Medicine Fellowship, HSS"
              />
            </Field>
            <Field label="Patient Population">
              <input style={s.input} value={form.patientPopulation || ""} onChange={(e) => setField("patientPopulation", e.target.value)} placeholder="e.g., Adult athletes, seniors with OA" />
            </Field>
          </Section>
        )}

        {/* ─── Insurance & Demographics ───────── */}
        <Section title="Insurance & Demographics">
          <Field label="Insurance Accepted">
            <TagInput
              items={form.insurancesAccepted}
              input={insuranceInput}
              setInput={setInsuranceInput}
              onAdd={() => addToList("insurancesAccepted", insuranceInput, setInsuranceInput)}
              onRemove={(item) => removeFromList("insurancesAccepted", item)}
              placeholder="e.g., BCBS, Aetna, Medicare"
            />
          </Field>
          <Field label="Languages Spoken">
            <TagInput
              items={form.languages}
              input={languageInput}
              setInput={setLanguageInput}
              onAdd={() => addToList("languages", languageInput, setLanguageInput)}
              onRemove={(item) => removeFromList("languages", item)}
              placeholder="e.g., English, Spanish"
            />
          </Field>
          <div style={s.row}>
            <Field label="Minimum Age" flex={1}>
              <input type="number" style={s.input} value={form.ageRange.min} onChange={(e) => setAgeField("min", e.target.value)} />
            </Field>
            <Field label="Maximum Age" flex={1}>
              <input type="number" style={s.input} value={form.ageRange.max} onChange={(e) => setAgeField("max", e.target.value)} />
            </Field>
          </div>
        </Section>

        {/* ─── Availability ───────────────────── */}
        <Section title="Availability">
          <Field label="Accepting New Patients">
            <label style={s.toggle}>
              <input type="checkbox" checked={form.acceptingNewPatients} onChange={(e) => setField("acceptingNewPatients", e.target.checked)} />
              <span>{form.acceptingNewPatients ? "Yes, accepting new patients" : "Not accepting new patients"}</span>
            </label>
          </Field>
          <Field label="Days Available">
            <div style={s.daysRow}>
              {DAYS.map((day) => (
                <button
                  key={day}
                  type="button"
                  style={form.daysAvailable.includes(day) ? s.dayPillActive : s.dayPill}
                  onClick={() => toggleDay(day)}
                >
                  {day}
                </button>
              ))}
            </div>
          </Field>
          <Field label="Avg Days to Next Available Slot">
            <input type="number" style={s.input} value={form.avgDaysToNextAvailable} onChange={(e) => setField("avgDaysToNextAvailable", parseInt(e.target.value, 10) || 0)} />
          </Field>
        </Section>

        {/* ─── Free Text (for LLM re-rank) ────── */}
        <Section title="Clinical Profile" hint="Free-text fields help the AI match engine understand this physician's strengths">
          <Field label="Bio">
            <textarea style={s.textarea} value={form.bio} onChange={(e) => setField("bio", e.target.value)} rows={3} placeholder="Background, experience, achievements..." />
          </Field>
          <Field label="Clinical Interests">
            <textarea style={s.textarea} value={form.clinicalInterests} onChange={(e) => setField("clinicalInterests", e.target.value)} rows={3} placeholder="Specific conditions, treatments, or research interests..." />
          </Field>
          <Field label="Referral Preferences">
            <textarea style={s.textarea} value={form.preferences} onChange={(e) => setField("preferences", e.target.value)} rows={3} placeholder="What types of referrals are welcomed? Any limitations?" />
          </Field>
        </Section>

        {/* ─── Actions ────────────────────────── */}
        <div style={s.actions}>
          <button style={s.cancelBtn} onClick={() => navigate("physicians")}>Cancel</button>
          <button style={s.saveBtn} onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : isEdit ? "Save Changes" : "Create Physician"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function Section({ title, hint, children }) {
  return (
    <div style={s.section}>
      <div style={s.sectionTitle}>{title}</div>
      {hint && <div style={s.sectionHint}>{hint}</div>}
      {children}
    </div>
  );
}

function Field({ label, hint, flex, children }) {
  return (
    <div style={{ ...s.field, ...(flex ? { flex } : {}) }}>
      <label style={s.label}>{label}</label>
      {hint && <div style={s.fieldHint}>{hint}</div>}
      {children}
    </div>
  );
}

function TagInput({ items, input, setInput, onAdd, onRemove, placeholder }) {
  return (
    <div>
      <div style={s.tagList}>
        {items.map((item) => (
          <span key={item} style={s.tag}>
            {item}
            <span style={s.tagX} onClick={() => onRemove(item)}>×</span>
          </span>
        ))}
      </div>
      <input
        style={s.input}
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            onAdd();
          }
        }}
        placeholder={placeholder}
      />
    </div>
  );
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const s = {
  loading: { padding: 60, textAlign: "center", color: "#999", fontSize: 14 },
  header: {
    display: "flex", alignItems: "flex-start", gap: 16,
    padding: "20px 32px", borderBottom: "1px solid #e5e5e5",
  },
  backBtn: {
    padding: "6px 12px", background: "#fff", color: "#0f0f0f",
    border: "1px solid #e5e5e5", borderRadius: 6, fontSize: 13, fontWeight: 500,
  },
  title: { fontSize: 22, fontWeight: 600, letterSpacing: "-0.02em" },
  subtitle: { fontSize: 12, color: "#999", marginTop: 2, fontFamily: "monospace" },
  body: { padding: "24px 32px", maxWidth: 760 },
  section: {
    marginBottom: 28,
    paddingBottom: 24,
    borderBottom: "1px solid #f0f0f0",
  },
  sectionTitle: {
    fontSize: 14, fontWeight: 600, color: "#0f0f0f", marginBottom: 4,
    textTransform: "uppercase", letterSpacing: "0.03em",
  },
  sectionHint: { fontSize: 12, color: "#999", marginBottom: 14, lineHeight: 1.4 },
  row: { display: "flex", gap: 12, alignItems: "flex-end" },
  field: { marginBottom: 14 },
  label: { display: "block", fontSize: 12, fontWeight: 500, color: "#666", marginBottom: 6 },
  fieldHint: { fontSize: 11, color: "#999", marginBottom: 6, marginTop: -2 },
  input: {
    width: "100%", padding: "8px 12px", border: "1px solid #e5e5e5", borderRadius: 6,
    fontSize: 13, outline: "none", color: "#0f0f0f", background: "#fff",
    boxSizing: "border-box", fontFamily: "inherit",
  },
  textarea: {
    width: "100%", padding: "8px 12px", border: "1px solid #e5e5e5", borderRadius: 6,
    fontSize: 13, outline: "none", color: "#0f0f0f", background: "#fff",
    boxSizing: "border-box", fontFamily: "inherit", resize: "vertical",
  },
  tagList: { display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 },
  tag: {
    display: "inline-flex", alignItems: "center", gap: 6,
    padding: "4px 10px", borderRadius: 12, background: "#eef4ff", color: "#1d4ed8",
    fontSize: 12, fontWeight: 500,
  },
  tagX: { cursor: "pointer", fontSize: 14, lineHeight: 1, marginLeft: 2 },
  toggle: {
    display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#333",
    cursor: "pointer",
  },
  daysRow: { display: "flex", gap: 6, flexWrap: "wrap" },
  officeBlock: {
    padding: 14, border: "1px solid #e5e5e5", borderRadius: 8,
    marginBottom: 12, background: "#fafafa",
  },
  officeHeader: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    marginBottom: 10,
  },
  officeHeaderLabel: {
    fontSize: 11, fontWeight: 600, color: "#666",
    textTransform: "uppercase", letterSpacing: "0.05em",
  },
  removeBtn: {
    padding: "4px 10px", background: "#fff", color: "#b91c1c",
    border: "1px solid #fecaca", borderRadius: 4,
    fontSize: 11, fontWeight: 500, cursor: "pointer",
  },
  addBtn: {
    padding: "9px 16px", background: "#fff", color: "#0f0f0f",
    border: "1px dashed #d4d4d4", borderRadius: 6,
    fontSize: 13, fontWeight: 500, cursor: "pointer", width: "100%",
  },
  checkboxGrid: {
    display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8,
  },
  dayPill: {
    padding: "6px 14px", borderRadius: 16, border: "1px solid #e5e5e5", background: "#fff",
    fontSize: 12, fontWeight: 500, color: "#666", cursor: "pointer",
  },
  dayPillActive: {
    padding: "6px 14px", borderRadius: 16, border: "1px solid #0f0f0f", background: "#0f0f0f",
    fontSize: 12, fontWeight: 500, color: "#fff", cursor: "pointer",
  },
  subspecialtyGrid: {
    display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8,
  },
  subSpecPill: {
    display: "flex", alignItems: "center", gap: 8,
    padding: "10px 14px", borderRadius: 8,
    border: "1px solid #e5e5e5", background: "#fff",
    fontSize: 12, fontWeight: 500, color: "#444",
    cursor: "pointer", textAlign: "left", lineHeight: 1.3,
  },
  subSpecPillActive: {
    display: "flex", alignItems: "center", gap: 8,
    padding: "10px 14px", borderRadius: 8,
    border: "1px solid #1d4ed8", background: "#eef4ff",
    fontSize: 12, fontWeight: 500, color: "#1d4ed8",
    cursor: "pointer", textAlign: "left", lineHeight: 1.3,
  },
  subSpecCheck: {
    width: 14, height: 14, borderRadius: 3,
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: 11, fontWeight: 700, flexShrink: 0,
  },
  actions: {
    display: "flex", justifyContent: "flex-end", gap: 8,
    padding: "20px 0 40px",
  },
  cancelBtn: {
    padding: "9px 20px", background: "#fff", color: "#666",
    border: "1px solid #e5e5e5", borderRadius: 6, fontSize: 13, fontWeight: 500,
  },
  saveBtn: {
    padding: "9px 20px", background: "#0f0f0f", color: "#fff",
    border: "1px solid #0f0f0f", borderRadius: 6, fontSize: 13, fontWeight: 500,
  },
};
